<template lang="pug">
  modal.modal(:title='$t("shareKimpleFolderModal.title")' @closeModal='hideModal')
    p.dark-gray.text-normal.m4-top-2 {{ $t('shareKimpleFolderModal.description') }}
    share-modal-filters.m-bottom-1.m-top-2(@search='filterUsers')
    share-modal-list(
      source="shareKimpleFolderModal"
      :sourceProps='props'
      @selectAll='toggleAll'
      :loading='loadingUsers'
      :users='usersList'
      :selectedUsers='selectedUsers'
    )
      .row.user.middle(v-for='user in usersList' :key='user.id')
        checkbox.m-right-1(
          name='user'
          :checked='isUserSelected(user.id)'
          :updateCheck='() => toggleUser(user)'
          transparent
        )
        img.avatar(v-if='!user.isGroup' :src='user.avatar || defaultProfileImage')
        .group-avatar.row.middle.center(v-else)
          simple-svg(:filepath='groupIcon' width='20px' height='20px')
        .column.m-left-1
          .row.middle.ellipsis.username
            h2.text-normal.m-right-05 {{ user.name }}
            h2.text-normal.ellipsis(
              v-if='!user.isGroup && (user.position.positionId || user.schoolName)'
              :title='getPositionWithSchoolName(user.position, user.schoolName)'
            ) {{ getPositionWithSchoolName(user.position, user.schoolName) }}
          h4.text-small.dark-gray(v-if='!user.isGroup') {{ user.email }}
        button.m-left-auto.m-right-1.black(
          v-if='user.isGroup'
          @click='() => showEditSharingGroupModal(user.id)'
        ) {{ $t('shareResource.editGroup') }}
    .row.end.m4-top-7
      button.primary-button.with-icon(type='button' @click='shareFolderWithUsers')
        simple-svg.white-icon.m4-right-2(:filepath='shareIcon' width='20px' height='20px')
        | {{ $t('shareKimpleFolderModal.share') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import amplitude from 'amplitude-js'
import Modal from '@/components/Modal'
import defaultProfileImage from '@/assets/empty_profile.svg'
import shareIcon from '@/assets/ic_share.svg'
import groupIcon from '@/assets/ic_group.svg'
import Checkbox from '@/components/Inputs/Checkbox'
import ShareModalFilters from '@/components/ShareModalFilters'
import ShareModalList from '@/components/ShareModalList'
import { deepCopy } from '../../utils/object'
import userPosition from '@/mixins/userPosition'

export default {
  mixins: [userPosition],
  components: { Checkbox, Modal, ShareModalFilters, ShareModalList },
  data() {
    return {
      groupIcon,
      defaultProfileImage,
      shareIcon,
      loadingUsers: true,
      filters: {},
      selectedUsers: []
    }
  },
  computed: {
    ...mapState({
      props: state => state.behavior.shareKimpleFolderModal.props,
      mySharingGroups: state => state.sharingGroup.mySharingGroups,
      usersToShareFolder: state => state.kimpleFolders.usersInMySchoolsGroup,
      users: state => state.userInfo.searchedUsers,
      userInfo: state => state.userInfo.userInfo
    }),
    usersList() {
      const list = [
        ...this.users.map(user => ({
          id: user.userId,
          name: user.fullName,
          avatar: user.avatar,
          position: user.position,
          schoolName: user.schoolName,
          email: user.email,
          isGroup: false
        })),
        ...this.mySharingGroups.map(group => ({
          id: group.id,
          name: group.name,
          users: group.userSharingGroups,
          isGroup: true
        }))
      ]
      return list.sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  async created() {
    this.filters = { schoolId: this.userInfo.schoolId }
    await this.searchUsers(this.filters)
    await this.hydrateMySharingGroups()
    this.loadingUsers = false
  },
  methods: {
    ...mapActions([
      'searchUsers',
      'clearUsersFromMySchoolsGroup',
      'hydrateMySharingGroups',
      'showElement',
      'hideElement',
      'shareKimpleFolder'
    ]),
    showEditSharingGroupModal(groupId) {
      this.hideElement({ element: 'shareKimpleFolderModal' })
      this.showElement({
        element: 'editSharingGroupModal',
        props: { source: 'shareKimpleFolderModal', sourceProps: this.props, groupId }
      })
    },
    isUserSelected(id) {
      return Boolean(this.selectedUsers.find(user => user.id === id))
    },
    hideModal() {
      this.hideElement({ element: 'shareKimpleFolderModal' })
      this.clearUsersFromMySchoolsGroup()
    },
    resetSelectedUsers() {
      this.selectedUsers = []
    },
    toggleUser(newUser) {
      if (this.isUserSelected(newUser.id)) {
        this.selectedUsers = this.selectedUsers.filter(user => user.id !== newUser.id)
      } else {
        this.selectedUsers = [...this.selectedUsers, newUser]
      }
    },
    toggleAll() {
      if (this.selectedUsers.length === this.usersList.length) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = deepCopy(this.usersList)
      }
    },
    async filterUsers(filters) {
      this.resetSelectedUsers()
      this.loadingUsers = true
      this.filters = filters
      await this.searchUsers(this.filters)
      this.loadingUsers = false
    },
    async shareFolderWithUsers() {
      this.showElement({ element: 'fullPageLoader' })
      const recipientIds = []
      for (const user of this.selectedUsers) {
        if (!user.isGroup) recipientIds.push(user.email)
        else for (const member of user.users) recipientIds.push(member.user.email)
      }
      const success = await this.shareKimpleFolder({
        folderId: this.props.id,
        recipients: Array.from(new Set(recipientIds)),
        locale: this.props.locale,
        folderLocale: this.props.folderLocale
      })
      if (success) {
        amplitude.getInstance().logEvent('Share Kimple folder', {
          id: this.props.id,
          name: this.props.name
        })
      }
      this.hideModal()
      this.hideElement({ element: 'fullPageLoader' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables/_colors.scss';

.modal{
  --max-width: 680px;
}

.users{
  height: 300px;
  overflow-y: auto;
  .loader{
    height: 100%;
  }
  &>:nth-child(even){
    background-color: $light-gray;
  }
  .user{
    padding: 10px;
    cursor: pointer;
    height: 50px;
    .username{
      max-width: 400px;
    }
    .avatar{
      width: 30px;
      height: 30px;
      border-radius: 50%;
      object-fit: cover;
    }
    .group-avatar{
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 1px solid $gray;
    }
  }
}

.user-search {
  border: 1px solid $gray;
  border-radius: 6px;
  padding: 0 8px;
  position: relative;

  .input-base {
    margin: 0;
  }
}

::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: $white;
}

::-webkit-scrollbar-thumb {
  background: $gray;
  border-radius: 5px;
  height: 72px;
}
</style>
